'use client';
import { BottomSheet } from '@/components/ui/BottomSheet';
import { Credits } from '@/components/Credits';
import { useLocalTranslation } from '@/hooks/useLocalTranslation';
import { useNavigation } from '@/contexts/NavigationContext';
import { useClaimSheetStore } from '@/stores/claimSheetStore';
import { playEarnings } from '@/lib/uiSounds';
import { showErrorToast, showInfoToast } from '@/components/Toast';
import { formatApiError } from '@/utils/apiError';
import { useMyAchievements } from '../hooks/useMyAchievements';
import { useClaimableRewards, useClaimRewards } from '../hooks/useClaimableRewards';
import { pickTitle, useAchievementLang } from '../utils/pickLocale';
import { AchievementIcon } from './AchievementIcon';
import { AchievementClaimBannerView } from './AchievementClaimBanner';
import translations from './AchievementClaimBanner.translations';

export function AchievementClaimSheet() {
  const { t } = useLocalTranslation(translations);
  const isOpen = useClaimSheetStore((s) => s.isOpen);
  const close = useClaimSheetStore((s) => s.close);

  return (
    <BottomSheet isOpen={isOpen} onClose={close} title={t('title')} heightClass="h-auto max-h-[85vh]">
      {isOpen && <ClaimSheetBody onClose={close} />}
    </BottomSheet>
  );
}

function ClaimedReward({ coins, xp }: { coins: number; xp: number }) {
  const { t } = useLocalTranslation(translations);
  return (
    <>
      {!!coins && (
        <span className="inline-flex items-center gap-0.5 text-xs font-semibold text-price">
          +<Credits amount={coins} iconSize={11} />
        </span>
      )}
      {!!xp && <span className="text-xs font-semibold text-label-secondary">+{xp} {t('xp')}</span>}
    </>
  );
}

function ClaimSheetBody({ onClose }: { onClose: () => void }) {
  const { t } = useLocalTranslation(translations);
  const lang = useAchievementLang();
  const { data } = useClaimableRewards();
  const { data: mine } = useMyAchievements();
  const claim = useClaimRewards();
  const navigation = useNavigation();
  const markJustClaimed = useClaimSheetStore((s) => s.markJustClaimed);

  const unclaimed = (mine?.items ?? []).filter((a) => a.unlockedAt && !a.rewardClaimed);

  const onClaim = () => {
    if (claim.isPending) return;
    const keys = unclaimed.map((a) => a.key);
    claim.mutate(undefined, {
      onSuccess: (res) => {
        playEarnings();
        markJustClaimed(keys);
        showInfoToast(t('claimed'), <ClaimedReward coins={res.claimedCoins} xp={res.claimedXp} />);
        onClose();
      },
      onError: (err) => {
        console.error('Failed to claim achievement rewards:', formatApiError(err));
        showErrorToast(t('claimFailed'), t('claimFailedHint'));
      },
    });
  };

  const onDeposit = () => {
    onClose();
    navigation.navigate('payment');
  };

  return (
    <div className="space-y-4 px-4 pb-6">
      {data && data.count > 0 && (
        <AchievementClaimBannerView
          data={data}
          claiming={claim.isPending}
          onClaim={onClaim}
          onDeposit={onDeposit}
        />
      )}

      {unclaimed.length > 0 && (
        <div className="space-y-1.5">
          {unclaimed.map((a) => (
            <div key={a.key} className="flex items-center gap-3 rounded-xl bg-fill-tertiary/60 p-2">
              <div className="relative h-10 w-10 shrink-0 overflow-hidden rounded-lg">
                <AchievementIcon iconFile={a.iconFile} alt="" fill className="object-contain" />
              </div>
              <span className="min-w-0 flex-1 truncate text-sm text-label-primary">
                {pickTitle(a.i18n, lang)}
              </span>
              {!!a.reward.credits && (
                <Credits
                  amount={a.reward.credits}
                  iconSize={11}
                  className="shrink-0 text-xs text-label-secondary"
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
